import type * as THREE from 'three';
import {
  FORGE,
  SALVAGE_DUST,
  SALVAGE_PLUS_BP,
  INCURSION,
  INCURSION_RINGS,
  INCURSION_SITES,
  MAX_RELICS,
  MAX_LEVEL,
  XP_TO_NEXT,
  type HeroClass,
  type IncursionRisk,
  INCURSION_TIERS,
  attrValue,
  type GearAttr, type GearBaseId, AILMENT_CURE, CELLARS, PLUNDER, MAZE, ROSTER, BASTION_OMENS, BASTION_OMENS_FROM, type IncursionOmen } from '@somnhold/content';
import {
  dayNodes,
  groupReached,
  dayOmen,
  waveSky,
  dayLootBase,
  incursionMission,
  dungeonOpen,
  eraOf,
  forgeMaxPlus,
  forgedOf,
  itemAttrs,
  gearMods,
  heroLook,
  heroStats,
  type CampaignCommand,
  type CampaignState,
  type HeroState,
  type ItemInstance,
  type PlayerState,
  tierOpen,
  cryptTierOpen,
  deepCryptOpen,
  cellarsOpen,
  mazeOpen,
  mazeRecord,
  pointsSpent,
  heroName,
} from '@somnhold/sim';
import { bellTrophyBox } from './cellars.ts';
import { tableTrophyBox } from './furrows.ts';
import { campaignDay } from '@somnhold/protocol';
import { audio } from '../audio/sfx.ts';
import { t } from '../i18n.ts';
import { clear, h } from '../ui/dom.ts';
import { fmtDuration } from '../ui/format.ts';
import { ICONS } from '../ui/icons.ts';
import { heroKey } from '@somnhold/render';
import { thumb } from '../ui/thumbs.ts';
import { incursionLoot } from './incursion-loot.ts';
import { levelGains } from './level-gains.ts';
import { loadoutEditor } from './loadout.ts';
import { skillTree, statCaps } from './skill-tree.ts';
import { classPicker } from '../account.ts';
import { effectBlock, effectDesc, effectStrength, paceBadge, relicCard, settingBlock } from './armory.ts';
import { titanMap } from './titan-map.ts';
import type { CampaignService } from './service.ts';
import { RUNE, SLOT_GLYPH, forgeRuined, gearPane, itemName } from './gear-sheet.ts';
import { freshIds, onFreshChange } from './fresh-gear.ts';

export { RUNE };

type Cmd = (c: CampaignCommand) => Promise<boolean>;


/** Awakening stages read as roman numerals. */
const ROMAN = ['', 'I', 'II', 'III', 'IV'];
const WEAPON_ICON: Record<string, string> = { warrior: ICONS.sword, artificer: ICONS.hammer, mage: ICONS.staff, ranger: ICONS.bow, cleric: ICONS.sunStaff };
const RELICS = ['fateDie', 'vigilGrimoire', 'vigilLantern', 'dreamerRing', 'warHorn', 'ashAmulet'] as const;


/** A weapon's or off-hand piece's icon by its base (pieces from before bases go by the hero's class). */
const BASE_ICON: Partial<Record<GearBaseId, string>> = {
  longsword: ICONS.sword, greatsword: ICONS.sword, handaxe: ICONS.axe, greataxe: ICONS.axe, offAxe: ICONS.axe,
  mace: ICONS.mace, warhammer: ICONS.hammer, maul: ICONS.hammer, wand: ICONS.staff, staff: ICONS.staff,
  quarterstaff: ICONS.staff, sickle: ICONS.briarLash, shortbow: ICONS.bow, longbow: ICONS.bow, heavyCrossbow: ICONS.bow,
  shield: ICONS.shield, grimoire: ICONS.tome, orb: ICONS.frostOrb, holySymbol: ICONS.sunStaff, quiver: ICONS.arrowRain,
  toolkit: ICONS.wrench, totem: ICONS.guardTotem,
};
const slotIcon = (item: ItemInstance, cls: string): string =>
  (item.base && BASE_ICON[item.base]) || (item.slot === 'weapon' ? (WEAPON_ICON[cls] ?? ICONS.sword) : SLOT_GLYPH[item.slot]);

/**
 * What the day's omen does to the bastion's waves (docs/PRESAGIOS.md §5): its own line where it changes them, the
 * storm's where this bastion can't raise a lightning rod yet, and one that says it leaves them be. Nothing before
 * the omens reached the waves.
 */
const bastionLine = (s: CampaignState, omen: IncursionOmen, day: number, now: number): HTMLElement | '' => {
  if (now < BASTION_OMENS_FROM) return '';
  const key = waveSky(s, day, now) ? `omen.${omen}.bastion` : BASTION_OMENS[omen] ? `omen.${omen}.bastionLater` : 'omen.bastionNone';
  return h('p.small.omen-bastion', { 'data-test': 'omen-bastion' }, t(key));
};

/** The item just forged: its card plays the forge flash once on the next render. */
let justForged = 0;
/** Plays the forge flash on this piece's card at the next render (a piece just forged or awakened). */
export const flashItem = (id: number): void => {
  justForged = id;
};

export const itemCard = (item: ItemInstance, cls: string, actions: (HTMLElement | '')[], p?: PlayerState): HTMLElement => {
  // Shown with the forge's pluses added; the forged part of each attribute is called out.
  const forged = forgedOf(item);
  const attrs = Object.entries(itemAttrs(item)).map(([k, v]) => {
    // Certeza counts for a share of its roll (attrValue): show what it's worth.
    const txt = t(`attr.${k}`, { v: Math.round(attrValue(k as GearAttr, v ?? 0) / 10) / 10 });
    const n = forged[k as keyof typeof forged] ?? 0;
    return n > 0 ? `${txt} ${t('hero.forged', { v: attrValue(k as GearAttr, n * FORGE.perPlusBp) / 100 })}` : txt;
  });
  // A forged piece looks it: its glow grows with each plus, and runes light up on the icon.
  const tier = item.plus >= 5 ? 3 : item.plus >= 3 ? 2 : item.plus >= 1 ? 1 : 0;
  const flash = justForged === item.id;
  if (flash) justForged = 0;
  return h(
    `div.item${tier ? `.forge-t${tier}` : ''}${flash ? '.forged-now' : ''}${item.awaken ? `.awake.awake-${item.awaken}` : ''}`,
    { style: `--rune:${RUNE[item.rarity]}`, 'data-slot': item.slot, 'data-plus': String(item.plus), ...(item.awaken ? { 'data-awaken': String(item.awaken) } : {}) },
    h('span.item-ico', { html: slotIcon(item, cls) }, item.plus ? h('span.item-runes', {}, ...Array.from({ length: item.plus }, () => h('i'))) : '', flash ? h('span.forge-sparks', {}, ...Array.from({ length: 10 }, (_, i) => h('i', { style: `--a:${i * 36}deg` }))) : ''),
    h(
      'div.item-main',
      {},
      h('div.item-head', {}, h('b', {}, itemName(item, cls as HeroClass), item.plus ? h('span.item-plus', {}, ` +${item.plus}`) : ''), h('span.rarity', {}, item.awaken ? t('uawaken.stage', { n: ROMAN[item.awaken] ?? String(item.awaken) }) : t(`rarity.${item.rarity}`))),
      h('div.small', {}, attrs.join(' · ')),
      p ? effectBlock(p, item) : item.effect ? h('div.small.effect', {}, h('b', {}, t(`effect.${item.effect}`)), ' — ', effectDesc(item.effect, effectStrength(item))) : '',
      settingBlock(item),
      item.unique ? h('div.small.effect.unique-fx', {}, t(`unique.${item.unique}.desc`)) : '',
      item.unique ? h('div.small.bound', {}, t('unique.bound')) : '',
      actions.some(Boolean) ? h('div.actions', {}, ...actions) : '',
    ),
  );
};

/** Hero sheet: who you are, the skill tree, what you wear and the relics found. */
// What the roster is asking before it acts (survives the sheet re-rendering): a new hero's class, or one to retire.
let rosterAsk: { kind: 'new'; cls: HeroClass | null; name: string } | { kind: 'retire'; index: number } | null = null;
/** The hero's name being written over the sheet's header (kept across a redraw while it is written). */
let naming: string | null = null;

/** The sheet opens without a question left over from the last time. */
export const resetRoster = (): void => {
  rosterAsk = null;
  naming = null;
};

/** A field for a hero's name: the player's own shows faded until one is written. */
const nameInput = (value: string, fallback: string, onInput: (v: string) => void): HTMLInputElement => {
  const el = h('input.hero-name-input', { type: 'text', maxlength: String(ROSTER.nameMax), value, placeholder: fallback, autocomplete: 'off', spellcheck: 'false', 'aria-label': t('hero.nameLabel'), 'data-test': 'hero-name-input' }) as HTMLInputElement;
  el.addEventListener('input', () => onInput(el.value));
  return el;
};

/** A hero's name with what they earned to be called: «de la campana» once the night of the bell is done (El Párpado 4). */
export const titledName = (p: PlayerState): string => (p.hero.bell ? `${heroName(p)} ${t('hero.bellTitle')}` : heroName(p));

/** The sheet's name: the hero's own (or its player's), with a quill to write another over it. */
const nameHead = (p: PlayerState, cmd: Cmd, rerender: () => void): HTMLElement => {
  if (naming === null)
    return h(
      'div.hs-name',
      {},
      h('h3', {}, titledName(p)),
      h('button.hs-quill', { type: 'button', 'data-test': 'hero-name-edit', title: t('hero.rename'), 'aria-label': t('hero.rename'), html: ICONS.quill, onclick: () => ((naming = p.hero.name ?? ''), rerender()) }),
    );
  let done = false;
  const input = nameInput(naming, p.displayName, (v) => (naming = v));
  const save = async (): Promise<void> => {
    if (done) return;
    done = true;
    const name = input.value.trim();
    naming = null;
    if (name !== (p.hero.name ?? '') && (await cmd({ t: 'nameHero', name }))) audio.play('click', 0.5);
    rerender();
  };
  const cancel = (): void => {
    if (done) return;
    done = true;
    naming = null;
    rerender();
  };
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void save();
    else if (e.key === 'Escape') cancel();
  });
  // Leaving the field keeps what was written (a redraw that takes the field away is not leaving it).
  input.addEventListener('blur', () => input.isConnected && void save());
  const form = h('div.hs-name.editing', {}, input, h('button.hs-quill.ok', { type: 'button', 'data-test': 'hero-name-save', title: t('hero.renameSave'), 'aria-label': t('hero.renameSave'), html: ICONS.check, onpointerdown: (e: Event) => e.preventDefault(), onclick: () => void save() }));
  requestAnimationFrame(() => {
    if (!input.isConnected) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  });
  return form;
};

/** What a hero would take with it if retired, in words. */
const heroLoss = (hero: HeroState): string[] => {
  const items = Object.values(hero.gear).filter(Boolean).length + hero.bag.length;
  const relics = hero.relics.length + (hero.relicBag?.length ?? 0);
  return [
    hero.level > 1 ? t('roster.lose.levels', { n: hero.level }) : '',
    Object.keys(hero.skills).length ? t('hero.lose.tree') : '',
    items ? t(items === 1 ? 'hero.lose.item' : 'hero.lose.items', { n: items }) : '',
    relics ? t(relics === 1 ? 'hero.lose.relic' : 'hero.lose.relics', { n: relics }) : '',
  ].filter(Boolean);
};

/**
 * The player's heroes in this campaign (up to ROSTER.max): the one in play, the ones waiting on the bench
 * (play with one, or retire it to make room) and a free place for a new one, who starts at level 1.
 */
const roster = (p: PlayerState, renderer: THREE.WebGLRenderer, cmd: Cmd, rerender: () => void): HTMLElement => {
  const bench = p.bench ?? [];
  const run = async (c: CampaignCommand, sound: 'horn' | 'crumble'): Promise<void> => {
    if (await cmd(c)) {
      rosterAsk = null;
      audio.play(sound, 0.5);
    }
    rerender();
  };
  const card = (hero: HeroState, extra: HTMLElement | string, active: boolean): HTMLElement =>
    h(
      `div.roster-hero${active ? '.active' : ''}`,
      { 'data-test': active ? 'roster-active' : 'roster-bench' },
      h('img.roster-pic', { src: thumb(renderer, heroKey(hero.heroClass, hero.level, false, heroLook(hero)), 96), alt: '' }),
      hero.name
        ? h('div.roster-id', {}, h('b', {}, hero.name), h('span.small', {}, t(`class.${hero.heroClass}`), ' · ', t('hero.level', { n: hero.level })))
        : h('div.roster-id', {}, h('b', {}, t(`class.${hero.heroClass}`)), h('span.small', {}, t('hero.level', { n: hero.level }))),
      extra,
    );
  const list = h('div.roster-list');
  list.append(card(p.hero, h('span.roster-tag', {}, t('roster.playing')), true));
  bench.forEach((b, i) => {
    const asking = rosterAsk?.kind === 'retire' && rosterAsk.index === i;
    list.append(
      card(
        b.hero,
        h(
          'div.roster-acts',
          {},
          h('button.btn.small', { 'data-test': 'roster-play', onclick: () => void run({ t: 'switchHero', index: i }, 'horn') }, t('roster.play')),
          h('button.btn.small.ghost', { 'data-test': 'roster-retire', disabled: asking, onclick: () => ((rosterAsk = { kind: 'retire', index: i }), rerender()) }, t('roster.retire')),
        ),
        false,
      ),
    );
    // The question sits right under the hero it is about.
    if (asking) {
      const lost = heroLoss(b.hero);
      list.append(
        h(
          'div.roster-ask',
          { 'data-test': 'roster-retire-ask' },
          h('p', {}, h('b', {}, t('roster.retireHint', { cls: t(`class.${b.hero.heroClass}`).toLowerCase(), n: b.hero.level }))),
          h('p.hint', {}, lost.length ? t('roster.retireLose', { what: lost.join(', ') }) : t('roster.retireNothing')),
          h(
            'div.actions',
            {},
            h('button.btn.small.ghost', { onclick: () => ((rosterAsk = null), rerender()) }, t('tree.respecNo')),
            h('button.btn.small.danger', { 'data-test': 'roster-retire-yes', onclick: () => void run({ t: 'retireHero', index: i }, 'crumble') }, t('roster.retireYes')),
          ),
        ),
      );
    }
  });
  const free = ROSTER.max - 1 - bench.length;
  for (let i = 0; i < free; i++) {
    list.append(
      h(
        'button.roster-hero.empty',
        { 'data-test': 'roster-new', disabled: rosterAsk?.kind === 'new', onclick: () => ((rosterAsk = { kind: 'new', cls: null, name: '' }), rerender()) },
        h('span.roster-plus', {}, '+'),
        h('div.roster-id', {}, h('b', {}, t('roster.new')), h('span.small', {}, t('roster.newSub'))),
      ),
    );
  }
  const wrap = h('section.roster', { 'data-test': 'roster' }, h('div.sec-title', {}, h('h4', {}, t('roster.title')), h('span.small', {}, `${1 + bench.length} / ${ROSTER.max}`)), list);
  const ask = rosterAsk;
  if (ask?.kind === 'new' && free > 0) {
    const go = h(
      'button.btn.small',
      { 'data-test': 'roster-new-yes', disabled: !ask.cls, onclick: () => ask.cls && void run({ t: 'newHero', cls: ask.cls, ...(ask.name.trim() ? { name: ask.name.trim() } : {}) }, 'horn') },
      ask.cls ? t('roster.newYes', { cls: t(`class.${ask.cls}`) }) : t('roster.pick'),
    );
    wrap.append(
      h(
        'div.roster-ask',
        {},
        h('p', {}, h('b', {}, t('roster.pick'))),
        classPicker(renderer, p.hero.heroClass, (c) => {
          ask.cls = c;
          go.toggleAttribute('disabled', false);
          go.textContent = t('roster.newYes', { cls: t(`class.${c}`) });
        }),
        h('label.hero-name-field', {}, h('span.small', {}, t('hero.nameLabel')), nameInput(ask.name, p.displayName, (v) => (ask.name = v))),
        h('p.hint', {}, t('roster.newHint', { cls: t(`class.${p.hero.heroClass}`).toLowerCase() })),
        h('div.actions', {}, h('button.btn.small.ghost', { onclick: () => ((rosterAsk = null), rerender()) }, t('tree.respecNo')), go),
      ),
    );
  } else if (!ask) wrap.append(h('p.hint', {}, t('roster.hint')));
  return wrap;
};

/**
 * Conditions that last days (docs/CRIATURAS.md §5): the hero's own, with how long it has left and how to cure
 * it, and for a cleric, the friends who carry one (cured for free once a day, even with them away).
 */
const ailmentBox = (svc: CampaignService, p: PlayerState, cmd: Cmd, rerender: () => void): HTMLElement | null => {
  const s = svc.state;
  const now = svc.now();
  const sick = (x: PlayerState): boolean => !!x.hero.ailment && x.hero.ailment.until > now && !x.left;
  const cleric = p.hero.heroClass === 'cleric';
  const clericReady = cleric && (p.hero.curedAt === undefined || now - p.hero.curedAt >= AILMENT_CURE.clericHours * 3_600_000);
  const temple = Object.values(s.cells).some((c) => c.built?.structure === 'temple' && c.built.hp > 0);
  const friends = cleric ? s.players.filter((x) => x.id !== p.id && sick(x)) : [];
  if (!sick(p) && !friends.length) return null;
  const run = async (target: string): Promise<void> => {
    if (await cmd({ t: 'cure', target })) rerender();
  };
  const box = h('section.ailment-box', { 'data-test': 'ailment' });
  const a = p.hero.ailment;
  if (a && sick(p)) {
    box.append(
      h('h5', {}, t(`ailment.${a.kind}`)),
      h('p.small', {}, t(`ailment.${a.kind}.desc`), ' ', t('ailment.left', { t: fmtDuration(a.until - now) })),
      clericReady
        ? h('button.btn.small.stone', { 'data-test': 'cure', onclick: () => void run(p.id) }, t('ailment.cureSelf'))
        : temple
          ? h('button.btn.small.stone', { 'data-test': 'cure', disabled: s.storage.dust < AILMENT_CURE.templeDust, onclick: () => void run(p.id) }, t('ailment.cureTemple', { n: AILMENT_CURE.templeDust }))
          : h('p.small.hint', {}, t('ailment.how')),
    );
  }
  if (friends.length) {
    box.append(h('h5', {}, t('ailment.friends')), clericReady ? '' : h('p.small.hint', {}, t('ailment.clericRest', { t: fmtDuration((p.hero.curedAt ?? now) + AILMENT_CURE.clericHours * 3_600_000 - now) })));
    for (const f of friends) {
      const k = f.hero.ailment?.kind ?? 'ashCurse';
      box.append(h('div.forge-row', {}, h('span.small', {}, `${heroName(f)} · ${t(`ailment.${k}`)}`), h('button.btn.small.stone', { 'data-test': 'cure-friend', disabled: !clericReady, onclick: () => void run(f.id) }, t('ailment.cureFriend'))));
    }
  }
  return box;
};

type HeroTab = 'hero' | 'skills' | 'gear' | 'relics';
const HERO_TABS: { k: HeroTab; icon: string }[] = [
  { k: 'hero', icon: ICONS.hero },
  { k: 'skills', icon: ICONS.tome },
  { k: 'gear', icon: ICONS.armor },
  { k: 'relics', icon: ICONS.fateDie },
];
// The tab last looked at: the sheet reopens there and survives its own re-rendering.
let heroTab: HeroTab = 'hero';

/** Opens the sheet on this tab next time (the advice about points to spend goes straight to the skills). */
export const showHeroTab = (k: HeroTab): void => {
  heroTab = k;
};

/**
 * The sheet's tabs and the one open below them. Only the open tab is built; switching keeps the bar on
 * screen when it was stuck to the top, so the new tab starts at its own beginning.
 */
const sheetTabs = (panes: Record<HeroTab, () => HTMLElement>, free: number, fresh: () => number): HTMLElement[] => {
  const mark = h('div.hs-tabs-mark');
  const panel = h('div.hs-panel', { role: 'tabpanel', id: 'hs-panel' });
  const bar = h('div.hs-tabs', { role: 'tablist', 'aria-label': t('hero.tabs') });
  const show = (k: HeroTab, user: boolean): void => {
    const scroller = bar.closest('.body');
    const stuck = !!scroller && user && mark.getBoundingClientRect().top < scroller.getBoundingClientRect().top;
    heroTab = k;
    for (const b of bar.children as HTMLCollectionOf<HTMLButtonElement>) {
      const on = b.dataset.sheetTab === k;
      b.classList.toggle('on', on);
      b.setAttribute('aria-selected', String(on));
      b.tabIndex = on ? 0 : -1;
    }
    clear(panel);
    panel.append(panes[k]());
    if (stuck && scroller) scroller.scrollTop += mark.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
  };
  // Arrows, Home and End move between tabs, as in any tab list.
  const keys = (e: KeyboardEvent): void => {
    const i = HERO_TABS.findIndex((x) => x.k === heroTab);
    const n = HERO_TABS.length;
    const to = e.key === 'ArrowRight' ? (i + 1) % n : e.key === 'ArrowLeft' ? (i + n - 1) % n : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
    if (to < 0) return;
    e.preventDefault();
    show((HERO_TABS[to] as (typeof HERO_TABS)[number]).k, true);
    (bar.children[to] as HTMLElement).focus();
  };
  const freshBadge = h('span.hs-tab-badge', { 'data-test': 'tab-fresh' });
  const showFresh = (): void => {
    const n = fresh();
    freshBadge.hidden = n === 0;
    freshBadge.textContent = String(n);
    freshBadge.title = t('bag.freshN', { n });
  };
  // Until the sheet goes (the next look after it was on screen finds the badge gone, and lets go).
  let shown = false;
  const off = onFreshChange(() => {
    if (freshBadge.isConnected) shown = true;
    else if (shown) return off();
    showFresh();
  });
  for (const { k, icon } of HERO_TABS) {
    bar.append(
      h(
        'button.hs-tab',
        { type: 'button', role: 'tab', 'aria-controls': 'hs-panel', 'data-sheet-tab': k, onclick: () => show(k, true), onkeydown: keys as EventListener },
        h('span.hs-tab-ico', { html: icon }),
        h('span.hs-tab-name', {}, t(`hero.tab.${k}`)),
        // Points waiting in the tree say so from any tab.
        k === 'skills' && free > 0 ? h('span.hs-tab-badge', { 'data-test': 'tab-points', title: t(free === 1 ? 'hero.point' : 'hero.points', { n: free }) }, String(free)) : '',
        // And new pieces in the bag (gifts, loot not looked at yet), kept up as they're looked at.
        k === 'gear' ? freshBadge : '',
      ),
    );
  }
  show(heroTab, false);
  showFresh();
  return [mark, bar, panel];
};

export const heroPanel = (svc: CampaignService, renderer: THREE.WebGLRenderer, cmd: Cmd, rerender: () => void): HTMLElement => {
  const s = svc.state;
  const p = s.players.find((x) => x.id === svc.me) as PlayerState;
  const hero = p.hero;
  const cls = hero.heroClass;
  const stats = heroStats({ pid: p.id, name: '', cls, level: hero.level, control: 'live', powerBp: 10000, post: 0, gear: gearMods(hero) });
  const next = hero.level < MAX_LEVEL ? (XP_TO_NEXT[hero.level] as number) : 0;
  const wrap = h('div.hero-sheet');

  // Header, on every tab: portrait, level and experience.
  const cdr = Math.round((10000 - stats.cooldownBp) / 100);
  const stat = (icon: string, value: string, label: string, key = ''): HTMLElement => h('div.stat', key ? { 'data-stat': key } : {}, h('span.stat-ico', { html: icon }), h('b', {}, value), h('span', {}, label));
  wrap.append(
    h(
      'div.hs-head',
      {},
      h('div.hs-portrait', {}, h('img', { src: thumb(renderer, heroKey(cls, hero.level, false, heroLook(hero))), alt: '' }), h('span.hs-level', { title: t('hero.level', { n: hero.level }) }, String(hero.level))),
      h(
        'div.hs-id',
        {},
        nameHead(p, cmd, rerender),
        h('div.hs-class', {}, t(`class.${cls}`), ' · ', t('hero.level', { n: hero.level })),
        next ? h('div.bar.blue.hs-xp', {}, h('i', { style: `width:${(100 * hero.xp) / next}%` })) : '',
        h('div.small', {}, next ? t('hero.xpLeft', { n: next - hero.xp, l: hero.level + 1 }) : t('hero.maxLevel')),
        next ? h('div.small.hs-next', { 'data-test': 'next-level' }, t('hero.nextLevel', { l: hero.level + 1, gains: levelGains(p, hero.level, hero.level + 1).join(' · ') })) : '',
        paceBadge(s, p),
      ),
    ),
  );
  // A condition to cure never hides behind a tab.
  const ail = ailmentBox(svc, p, cmd, rerender);
  if (ail) wrap.append(ail);
  // Gear and relics both ask whether the forge stands.
  const cap = forgeMaxPlus(s);
  const forgeBuilt = cap > 0;

  // The tabs: who the hero is, what it knows, what it wears and the relics it carries.
  const panes: Record<HeroTab, () => HTMLElement> = {
    hero: () => {
      const box = h('div.hs-pane');
      box.append(
        h(
          'div.hs-stats',
          {},
          stat(ICONS.heart, String(stats.maxHp), t('hero.stat.hp')),
          stat(ICONS.sword, String(stats.attack), t('hero.stat.attack')),
          // Every «+% damage» adds up and lands once on the base attack: the total, so a piece's share reads right.
          stat(ICONS.ember, `+${Math.round((stats.damageBp ?? 0) / 100)} %`, t('hero.stat.damage'), 'damage'),
          stat(ICONS.shield, `${Math.round(stats.armorBp / 100)} %`, t('hero.stat.armor'), 'armor'),
          stat(ICONS.haste, (12 / Math.max(1, stats.reload)).toFixed(1), t('hero.stat.speed')),
          stat(ICONS.hourglass, `${cdr > 0 ? '−' : ''}${cdr} %`, t('hero.stat.cooldown')),
        ),
        statCaps(hero),
      );
      if (s.status === 'active') box.append(roster(p, renderer, cmd, rerender));
      // The rest of the group: who they play, how far along they are, when they were last around.
      const allies = s.players.filter((x) => x.id !== p.id && !x.left);
      if (allies.length) {
        const now = svc.now();
        const list = h('div.allies');
        for (const a of allies.slice().sort((x, y) => y.hero.level - x.hero.level)) {
          const ago = now - a.lastActiveAt;
          list.append(
            h(
              'div.ally',
              { style: `--who:${a.color}` },
              h('img.ally-pic', { src: thumb(renderer, heroKey(a.hero.heroClass, a.hero.level, false, heroLook(a.hero)), 96), alt: '' }),
              h('div.ally-id', {}, h('b', {}, titledName(a)), h('span.small', {}, a.hero.name && a.hero.name !== a.displayName ? `${a.displayName} · ` : '', t(`class.${a.hero.heroClass}`), ' · ', ago < 10 * 60_000 ? t('hero.allyNow') : t('hero.allySeen', { t: fmtDuration(ago) }))),
              h('div.ally-relics', {}, ...a.hero.relics.map((r) => h(`span${(a.hero.awakened ?? []).includes(r) ? '.awake' : ''}`, { title: (a.hero.awakened ?? []).includes(r) ? `${t(`relic.${r}`)} · ${t('awaken.badge')}` : t(`relic.${r}`), html: ICONS[r] }))),
              h('span.ally-lvl', { title: t('hero.level', { n: a.hero.level }) }, String(a.hero.level)),
            ),
          );
        }
        box.append(h('div.sec-title', {}, h('h4', {}, t('hero.allies'))), list);
      }
      return box;
    },
    skills: () => h('div.hs-pane', {}, skillTree(s, p, cmd, rerender), loadoutEditor(s, p, cmd, rerender)),
    gear: () => {
      const box = gearPane({ s, p, renderer, cmd, rerender, cap, ruined: forgeRuined(s) }, { dust: SALVAGE_DUST, plusBp: SALVAGE_PLUS_BP }, heroKey(cls, hero.level, false, heroLook(hero)));
      if (dungeonOpen(s)) box.append(chestStat(svc));
      return box;
    },
    relics: () => {
      const box = h('div.hs-pane');
      // Relics: a hero carries up to two; empty slots say where they come from.
      const relics = h('div.relics');
      for (let i = 0; i < MAX_RELICS; i++) {
        const r = hero.relics[i] as (typeof RELICS)[number] | undefined;
        relics.append(r ? relicCard(s, p, r, { kept: false, forge: forgeBuilt, cmd, rerender }) : h('div.relic', {}, h('span.relic-ico', {}, '?'), h('div', {}, h('b', {}, t('hero.relicFree')))));
      }
      box.append(h('div.sec-title', {}, h('h4', {}, t('hero.relics')), h('span.small', {}, `${hero.relics.length} / ${MAX_RELICS}`)), relics);
      // Relics won with both slots full wait in reserve: swap one in for a carried one.
      const bag = hero.relicBag ?? [];
      if (bag.length) {
        const reserve = h('div.relics.reserve');
        for (const r of bag) {
          const swap = h('div.relic-swap', {}, ...hero.relics.map((c) => h('button.btn.small.ghost', { 'data-test': 'relic-swap', onclick: async () => { if (await cmd({ t: 'relicSwap', relic: r, drop: c })) rerender(); } }, t('hero.relicSwap', { r: t(`relic.${c}`) }))));
          reserve.append(relicCard(s, p, r, { kept: true, forge: forgeBuilt, cmd, rerender, extra: swap }));
        }
        box.append(h('div.sec-title', {}, h('h4', {}, t('hero.relicReserve'))), reserve);
      }
      box.append(h('p.hint', {}, t('hero.relicsFrom', { n: RELICS.length })), h('p.hint', {}, forgeBuilt ? t('awaken.hint') : forgeRuined(s) ? t('forge.ruined') : t('awaken.needForge')));
      return box;
    },
  };
  wrap.append(...sheetTabs(panes, s.status === 'active' ? hero.skillPoints - pointsSpent(hero) : 0, () => freshIds(s, p).size));
  return wrap;
};

/** When the campaign day turns (the crypt chests come back): the first instant whose day is past today's. */
export const nextCampaignDay = (svc: CampaignService): number => {
  const now = svc.now();
  const today = campaignDay(svc.calendar(), now);
  let lo = now;
  let hi = now + 26 * 3_600_000;
  while (hi - lo > 60_000) {
    const mid = Math.floor((lo + hi) / 2);
    if (campaignDay(svc.calendar(), mid) > today) hi = mid;
    else lo = mid;
  }
  return hi;
};

/** The crypt chests at a glance: taken today or waiting, and when they come back. */
export const chestStat = (svc: CampaignService): HTMLElement => {
  const s = svc.state;
  const me = s.players.find((p) => p.id === svc.me);
  const day = svc.day();
  const back = new Date(nextCampaignDay(svc)).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const line = (taken: boolean, label: string): HTMLElement =>
    h(`span.chest${taken ? '.taken' : ''}`, { title: taken ? t('chest.takenTip', { t: back }) : t('chest.readyTip') }, h('span', { html: ICONS.chest }), label, ' ', h('b', {}, taken ? t('chest.taken', { t: back }) : t('chest.ready')));
  return h(
    'div.inc-stat.chests',
    { 'data-test': 'chest-stat' },
    h('span.label', {}, t('chest.label')),
    h('div.row', {}, line(me?.dungeonDay === day, t('chest.crypt')), cryptTierOpen(s) ? line(me?.dungeonDay2 === day, t('chest.crypt2')) : '', deepCryptOpen(s) ? line(me?.dungeonDay3 === day, t('chest.crypt3')) : ''),
  );
};

const NODE_ICON: Record<string, string> = { quarry: ICONS.stone, forest: ICONS.wood, crystal: ICONS.crystal, altar: ICONS.dust, rescue: ICONS.keep, lair: ICONS.skull, iron: ICONS.iron, fort: ICONS.hammer };
const OMEN_ICON: Record<string, string> = { fog: ICONS.omenFog, frenzy: ICONS.haste, ink: ICONS.skull, swarm: ICONS.multishot, storm: ICONS.storm, frost: ICONS.frostWall, bloodMoon: ICONS.omenMoon, starfall: ICONS.omenStar };

/** Places this player has already seen on the map; new ones clear the fog with an animation. */
const seenSites = (campaignId: string, open: number[]): Set<number> => {
  const key = `somnhold.sites.${campaignId}`;
  let seen: number[] | null = null;
  try {
    seen = JSON.parse(localStorage.getItem(key) ?? 'null') as number[] | null;
    localStorage.setItem(key, JSON.stringify(open));
  } catch {
    /* no storage: nothing animates */
  }
  return new Set(seen ? open.filter((x) => !seen.includes(x)) : []);
};

/**
 * The garrison cellars (docs/EXPEDICIONES.md), from the third Tide: a group expedition for three or four
 * guardians connected at once, so online only (a local campaign shows it shut, and why).
 */
export const cellarsCard = (svc: CampaignService, onCellars: (scene?: boolean) => void): HTMLElement | null => {
  const s = svc.state;
  if (!cellarsOpen(s)) return null;
  const online = svc.kind === 'server';
  const me = s.players.find((p) => p.id === svc.me);
  const claimed = me?.expeditionDay === svc.day();
  const seen = svc.presence();
  const name = (pid: string): string => s.players.find((p) => p.id === pid)?.displayName ?? '—';
  const room = seen?.rooms.find((r) => r.kind === 'expedition' && !r.started);
  const n = seen?.online.length ?? 0;
  const facts = h(
    'ul.crypt-facts',
    {},
    h('li', {}, h('span', { html: ICONS.hero }), t('exp.factParty', { min: CELLARS.minHeroes, max: CELLARS.maxHeroes })),
    online && seen ? h('li', { 'data-test': 'exp-online' }, h('span', { html: ICONS.globe }), n <= 1 ? t('exp.factOnlineOne') : t('exp.factOnline', { n })) : '',
    room ? h('li', {}, h('span', { html: ICONS.bell }), t('exp.factGathering', { names: room.players.map(name).join(', ') || '—' })) : '',
    h('li', {}, h('span', { html: ICONS.ghost }), t('exp.factDrop', { s: CELLARS.dropGraceTicks / 12 })),
    h('li', {}, h('span', { html: ICONS.chest }), claimed ? t('exp.factClaimed') : t('exp.factReward')),
  );
  const go = h('button.btn.crypt-go.exp-go', { 'data-test': 'exp-go', disabled: !online, onclick: () => onCellars() }, t('exp.go'));
  const again = online ? h('button.btn.small.ghost.exp-scene', { 'data-test': 'exp-scene', onclick: () => onCellars(true) }, t('exp.sceneAgain')) : '';
  return h(
    'section.crypt.open.exp-card',
    { 'data-test': 'exp-card' },
    h('div.crypt-art', { html: CELLARS_SVG }),
    h('div.crypt-body', {}, h('span.label', {}, t('exp.label')), h('h3', {}, t('exp.title')), h('p', {}, t('exp.pitch')), facts, go, again, online ? '' : h('p.crypt-lock', {}, h('span', { html: ICONS.lock }), t('exp.onlineOnly', { min: CELLARS.minHeroes, max: CELLARS.maxHeroes })), bellTrophyBox(s, true)),
  );
};

/**
 * The Furrows (docs/LABERINTOS.md), from the second Tide: the week's labyrinth, alone or with friends online. The card
 * says how much of this week's map the group has, its camps, whether the centre fell, and who went out.
 */
export const furrowsCard = (svc: CampaignService, onFurrows: (scene?: boolean) => void): HTMLElement | null => {
  const s = svc.state;
  if (!mazeOpen(s)) return null;
  const online = svc.kind === 'server';
  const day = svc.day();
  const rec = mazeRecord(s, day);
  const left = MAZE.weekDays - (day % MAZE.weekDays);
  const name = (pid: string): string => s.players.find((p) => p.id === pid)?.displayName ?? '—';
  const room = svc.presence()?.rooms.find((r) => r.kind === 'maze' && !r.started);
  const total = (MAZE.radius * (MAZE.radius + 1)) * 3 + 1 - 6;
  const pct = Math.min(100, Math.round((rec.seen.length * 100) / total));
  const facts = h(
    'ul.crypt-facts',
    {},
    h('li', { 'data-test': 'maze-week' }, h('span', { html: ICONS.hourglass }), left <= 1 ? t('maze.factWeekOne') : t('maze.factWeek', { n: left })),
    h('li', { 'data-test': 'maze-map' }, h('span', { html: ICONS.map }), t('maze.factMap', { p: pct }), ' · ', t('maze.factCamps', { k: rec.camps.length, n: MAZE.camps.length })),
    h('li', {}, h('span', { html: ICONS.skull }), rec.won ? t('maze.factCentre') : t('maze.factCentreNot')),
    rec.explorers.length ? h('li', {}, h('span', { html: ICONS.hero }), t('maze.factWho', { names: rec.explorers.map(name).join(', ') })) : '',
    room ? h('li', {}, h('span', { html: ICONS.globe }), t('maze.factGathering', { names: room.players.map(name).join(', ') || '—' })) : '',
    h('li', {}, h('span', { html: ICONS.chest }), rec.paid.includes(svc.me) ? t('maze.factPaid') : t('maze.factReward')),
    h('li', {}, h('span', { html: ICONS.hero }), t('maze.factParty', { max: MAZE.maxHeroes - 1 })),
  );
  const go = h('button.btn.crypt-go.maze-go', { 'data-test': 'maze-go', disabled: !online, onclick: () => onFurrows() }, t('maze.go'));
  const again = online ? h('button.btn.small.ghost.maze-scene', { 'data-test': 'maze-scene', onclick: () => onFurrows(true) }, t('maze.sceneAgain')) : '';
  return h(
    'section.crypt.open.maze-card',
    { 'data-test': 'maze-card' },
    h('div.crypt-art', { html: FURROWS_SVG }),
    h('div.crypt-body', {}, h('span.label', {}, t('maze.label')), h('h3', {}, t('maze.title')), h('p', {}, t('maze.pitch')), facts, go, again, online ? '' : h('p.crypt-lock', {}, h('span', { html: ICONS.lock }), t('maze.onlineOnly')), tableTrophyBox(s, true)),
  );
};

/** A furrow between two plates of the shell, seen from above: walls of stone, a lit path to a red waystone. */
const FURROWS_SVG = `<svg viewBox="0 0 120 120" aria-hidden="true"><rect width="120" height="120" fill="#0b0c10"/>
<g fill="#2a2d33" stroke="#41444c" stroke-width="2"><path d="M8 20h44v14H22v24h30v14H8z"/><path d="M68 20h44v52H98V34H68z"/><path d="M8 86h60v14H8zM82 86h30v14H82z"/><path d="M36 48h30v10H36z"/></g>
<path d="M14 112V78h46V64h28v34" stroke="#5a5d65" stroke-width="3" fill="none" stroke-dasharray="3 4"/>
<circle cx="88" cy="98" r="7" fill="#ff4f3a" opacity=".35"/><path d="M85 104v-12l3-3 3 3v12z" fill="#4a4c53"/><rect x="85" y="96" width="6" height="2" fill="#ff4f3a"/></svg>`;

/** The courtyard well going down into the cellars, and the bell hanging above it. */
const CELLARS_SVG = `<svg viewBox="0 0 120 120" aria-hidden="true"><defs><radialGradient id="wg" cx="50%" cy="80%" r="55%"><stop offset="0" stop-color="#5aa0c8" stop-opacity=".5"/><stop offset="1" stop-color="#5aa0c8" stop-opacity="0"/></radialGradient></defs>
<rect width="120" height="120" fill="#0b0c10"/><path d="M30 28h60M36 28v34M84 28v34" stroke="#4a4c53" stroke-width="4"/><path d="M60 28v14" stroke="#8a8d96" stroke-width="2"/>
<path d="M50 52a10 10 0 0120 0v6H50z" fill="#c8ccd4" stroke="#6b6f78" stroke-width="2"/><circle cx="60" cy="61" r="2.4" fill="#c8ccd4"/>
<ellipse cx="60" cy="86" rx="38" ry="12" fill="#24262b"/><ellipse cx="60" cy="86" rx="28" ry="8" fill="#07080a"/><ellipse cx="60" cy="88" rx="28" ry="8" fill="url(#wg)"/>
<path d="M22 86v18a38 12 0 0076 0V86" fill="#1b1d22" stroke="#41444c" stroke-width="2"/><path d="M34 99v8M48 102v8M62 103v8M76 102v8M88 99v8" stroke="#2c2f37" stroke-width="2"/></svg>`;


export const incursionPanel = (
  svc: CampaignService,
  onGo: (node: number, companions: string[], risk: IncursionRisk, tier: 1 | 2 | 3) => void,
  renderer: THREE.WebGLRenderer,
  onTogether: ((node: number, tier: 1 | 2 | 3) => void) | null = null,
): HTMLElement => {
  const s = svc.state;
  const me = s.players.find((p) => p.id === svc.me) as PlayerState;
  const day = svc.day();
  const nodes = dayNodes(s, day);
  const done = me.incursionDay === day ? me.incursionsToday : 0;
  const full = done < INCURSION.fullPerDay;
  const ranger = s.players.some((p) => p.hero.heroClass === 'ranger' && !p.left);
  const wrap = h('div.inc');
  const card = h('div.inc-card');
  const companions = s.players.filter((p) => p.id !== me.id && !p.left);
  // Up to three ghosts along; the first friend by default. Who is online right now is marked.
  let picked: string[] = companions[0] ? [companions[0].id] : [];
  const online = new Set(svc.presence()?.online ?? []);
  const omen = dayOmen(s, day);
  const fresh = seenSites(s.id, nodes.map((n) => n.id));
  const openedBy = s.explored?.by ?? {};
  const nameOf = (pid: string): string => s.players.find((p) => p.id === pid)?.displayName ?? '—';
  // Level II (Ascuas) where the group has opened it; the choice sticks while browsing places.
  let tier: 1 | 2 | 3 = 1;
  const won2 = new Set(s.explored?.won2 ?? []);
  const won3 = new Set(s.explored?.won3 ?? []);
  const pick = (id: number, scroll = false): void => {
    map.pick(id);
    const n = nodes.find((x) => x.id === id);
    clear(card);
    if (!n) return;
    const open2 = !n.locked && tierOpen(s, n.id, 2);
    const open3 = !n.locked && tierOpen(s, n.id, 3);
    const levels = ([1, 2, 3] as const).filter((k) => k === 1 || (k === 2 ? open2 : open3));
    const at: 1 | 2 | 3 = (levels as readonly number[]).includes(tier) ? tier : 1;
    const tierSel = open2
      ? h(
          'div',
          {},
          h(
            'div.tier-sel',
            { role: 'radiogroup', 'aria-label': t('ilevel.label') },
            ...levels.map((k) =>
              h(`button.tier-opt.t${k}${at === k ? '.on' : ''}`, { role: 'radio', 'aria-checked': String(at === k), 'data-tier': k, onclick: () => { tier = k; pick(id); } }, h('b', {}, t(`ilevel.${k}`)), h('span.small', {}, t(`ilevel.${k}.sub`)), k > 1 && (k === 2 ? won2 : won3).has(n.id) ? h('span.tier-won', { title: t(k === 3 ? 'ilevel.wonTip3' : 'ilevel.wonTip') }, '✓') : ''),
            ),
          ),
          !open3 && groupReached(s, INCURSION_TIERS[3].minGroup) ? h('p.small.tier-hint', {}, h('span', { html: ICONS.lock }), t('ilevel.winFirst3')) : '',
        )
      : groupReached(s, INCURSION_TIERS[2].minGroup) && !n.locked
        ? h('p.small.tier-hint', {}, h('span', { html: ICONS.lock }), t('ilevel.winFirst'))
        : '';
    const ring = INCURSION_RINGS[n.ring];
    const who = h('div.who');
    const count = h('span.small.who-count', {}, t('inc.companionCount', { n: picked.length, max: INCURSION.maxCompanions }));
    const choice = (id: string | null, pic: HTMLElement, name: string, sub: string): HTMLElement => {
      const on = id === null ? picked.length === 0 : picked.includes(id);
      const b = h(`button.who-opt${on ? '.on' : ''}`, {
        'data-who': id ?? '',
        onclick: () => {
          if (id === null) picked = [];
          else if (picked.includes(id)) picked = picked.filter((x) => x !== id);
          else if (picked.length < INCURSION.maxCompanions) picked = [...picked, id];
          for (const x of who.children) {
            const w = (x as HTMLElement).dataset.who ?? '';
            x.classList.toggle('on', w === '' ? picked.length === 0 : picked.includes(w));
          }
          count.textContent = t('inc.companionCount', { n: picked.length, max: INCURSION.maxCompanions });
        },
      }, pic, h('span', {}, h('b', {}, name, id && online.has(id) && id !== me.id ? h('i.online-dot', { title: t('inc.online') }) : ''), h('span.small', {}, sub)));
      return b;
    };
    who.append(choice(null, h('span.who-pic', { html: ICONS.hero }), t('inc.alone'), t('inc.aloneSub')));
    for (const c of companions) who.append(choice(c.id, h('img.who-pic', { src: thumb(renderer, heroKey(c.hero.heroClass, c.hero.level, false, heroLook(c.hero)), 96), alt: '' }), heroName(c), t('inc.ghostOf', { cls: t(`class.${c.hero.heroClass}`), n: c.hero.level })));
    const mult = (bp: number): string => String(bp / 10000).replace('.', t('num.decimal'));
    const by = openedBy[String(n.id)];
    card.className = `inc-card k-${n.kind} r${n.ring}${at === 2 ? '.ascuas' : at === 3 ? '.obsidian' : ''}`;
    card.append(
      h(
        'div.inc-banner',
        {},
        h('span.inc-medal', { html: NODE_ICON[n.kind] as string }),
        h(
          'div.inc-head',
          {},
          n.double && ranger ? h('span.inc-badge', {}, t('inc.double')) : '',
          h('h3', {}, t(`site.${n.id}`)),
          h('p', {}, h('b', {}, t(`inc.${n.kind}`)), ' · ', t(`inc.${n.kind}.desc`)),
        ),
      ),
      h(
        'div.inc-ring',
        {},
        h('span.ring-pips', {}, ...[1, 2, 3].map((k) => h(`i${k <= n.ring ? '.on' : ''}`))),
        h('div', {}, h('b', {}, t(`inc.ring.${n.ring}`)), h('p', {}, n.ring === 1 ? t('inc.ring.1.desc') : t(`inc.ring.${n.ring}.desc`, { mult: mult(ring.rewardBp), item: ring.itemBp / 100 }))),
      ),
      n.locked ? h('p.inc-locked', {}, h('span', { html: ICONS.lock }), t('inc.lockedRing', { n: INCURSION_RINGS[3].minGroup })) : '',
      tierSel,
      // What the next run here asks (each run draws its own): a plunder says so, up front.
      incursionMission(s, n.id, day) === 'plunder'
        ? h('div.inc-twist.plunder', { 'data-test': 'inc-mission' }, h('span.label', {}, t('plunder.title')), h('p', {}, t('plunder.twist', { n: PLUNDER.fullLoad })), at > 1 ? h('p.tier-twist', {}, t(`ilevel.${at}.twist`)) : '')
        : h('div.inc-twist', {}, h('span.label', {}, t('inc.howPlays')), h('p', {}, t(`inc.twist.${n.kind}`)), at > 1 ? h('p.tier-twist', {}, t(`ilevel.${at}.twist`)) : ''),
      h('div.inc-sec', {}, h('span.label', {}, t('inc.youGet')), h('div.inc-reward', {}, ...incursionLoot(s, me.id, day, n.id, 'normal', at).map(([k, v]) => h('span.loot', {}, h('span', { html: ICONS[k] }), h('b', {}, String(v)), h('span.small', {}, t(`res.${k}`))))), n.kind === 'lair' ? h('p.small', {}, t('inc.lairLoot')) : '', incursionMission(s, n.id, day) === 'plunder' ? h('p.small', {}, t('plunder.loot', { cap: Math.round(PLUNDER.capBp / 100) })) : '', at === 2 ? h('p.small.tier-loot', {}, h('span', { html: ICONS.ember }), t('ilevel.2.loot', { n: INCURSION_TIERS[2].embers, rare: INCURSION_TIERS[2].bossRareBp / 100 })) : '', at === 3 ? h('p.small.tier-loot', {}, h('span', { html: ICONS.shard }), t('ilevel.3.loot', { n: INCURSION_TIERS[3].shards, rare: INCURSION_TIERS[3].bossRareBp / 100 })) : ''),
      companions.length && !n.locked ? h('div.inc-sec', {}, h('span.label', {}, t('inc.companions'), ' ', count), who, h('p.small', {}, t('inc.companionsHint'))) : '',
      by ? h('p.small.inc-by', {}, by === me.id ? t('inc.openedByYou') : t('inc.openedBy', { name: nameOf(by) })) : '',
      h('button.btn.inc-go', { 'data-test': 'inc-go', disabled: n.locked, onclick: () => onGo(n.id, picked, 'normal', at) }, at === 2 ? t('ilevel.go') : at === 3 ? t('ilevel.go3') : t('inc.go')),
      onTogether && !n.locked && companions.length
        ? h('div.inc-together', {}, h('button.btn.ghost', { 'data-test': 'inc-together', onclick: () => onTogether(n.id, at) }, t('inc.together')), h('p.small', {}, t('inc.togetherHint')))
        : '',
    );
    // On phones the card sits under the map: bring the chosen place into view.
    if (scroll && window.matchMedia('(max-width: 640px)').matches) card.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  };
  const map = titanMap({
    nodes,
    openedBy,
    colorOf: (pid) => s.players.find((p) => p.id === pid)?.color ?? null,
    nameOf: (pid) => s.players.find((p) => p.id === pid)?.displayName ?? null,
    fresh,
    ranger,
    tierOf: (id) => (won3.has(id) ? 4 : tierOpen(s, id, 3) ? 3 : won2.has(id) ? 2 : tierOpen(s, id, 2) ? 1 : 0),
    onPick: (id) => pick(id, true),
  });
  const step = (icon: string, text: string): HTMLElement => h('li', {}, h('span', { html: icon }), h('span', {}, text));
  const total = Math.max(nodes.length, INCURSION_SITES.filter((x) => (x.minEra ?? 0) <= eraOf(s)).length);
  const stats = h(
    'div.inc-stats',
    {},
    h(
      'div.inc-stat',
      { title: t('inc.explored', { n: nodes.length, total }) },
      h('span.label', {}, t('inc.exploredLabel')),
      h('div.row', {}, h('b', {}, String(nodes.length), h('small', {}, ` / ${total}`)), h('span.bar', {}, h('i', { style: `width:${Math.round((100 * nodes.length) / Math.max(1, total))}%` }))),
    ),
    h(
      'div.inc-stat',
      {},
      h('span.label', {}, t('inc.todayLabel')),
      h('div.row', {}, h('span.pips', {}, ...Array.from({ length: INCURSION.fullPerDay }, (_, i) => h(`i${i < done ? '.used' : ''}`))), h('span.small', {}, full ? t('inc.todayLeftShort', { n: INCURSION.fullPerDay - done, max: INCURSION.fullPerDay }) : t('inc.todayReduced'))),
    ),
  );
  const sw = (cls: string, text: string, body = ''): HTMLElement => h('li', {}, h(`span.sw${cls}`, {}, body), text);
  const legend = h(
    'ul.inc-legend',
    {},
    sw('', t('inc.legend.near')),
    sw('.r2', t('inc.legend.mid')),
    sw('.r3', t('inc.legend.far')),
    nodes.some((n) => n.locked) ? sw('.locked', t('inc.legend.locked')) : '',
    ranger && nodes.some((n) => n.double) ? sw('.x2', t('inc.double'), '×2') : '',
    nodes.length < total ? sw('.unknown', t('inc.legend.unknown')) : '',
    // The coloured dot on a place: the friend who opened it (their colour).
    Object.keys(s.explored?.by ?? {}).length ? sw('.opener', t('inc.legend.opener')) : '',
  );
  wrap.append(
    h('ol.inc-how', {}, step(ICONS.map, t('inc.how1')), step(ICONS.shieldBash, t('inc.how2')), step(ICONS.globe, t('inc.how3map'))),
    h('div.inc-omen', { 'data-omen': omen }, h('span.om-ico', { html: OMEN_ICON[omen] as string }), h('div', {}, h('span.label', {}, t('omen.title')), h('b', {}, t(`omen.${omen}`)), h('p', {}, t(`omen.${omen}.desc`)), bastionLine(s, omen, day, svc.now()), h('p.small.day-base', { 'data-test': 'day-base', title: t('loot.dayBaseHint') }, h('span', { html: ICONS.sparkle }), t('loot.dayBase', { base: t(`gear.base.${dayLootBase(s, day)}`) })))),
    h('div.inc-main', {}, h('div.inc-mapcol', {}, map.el, legend, stats), card),
  );
  // Start on a freshly opened place, else a doubled one, else the nearest.
  const first = nodes.find((n) => fresh.has(n.id) && !n.locked) ?? nodes.find((n) => n.double && ranger && !n.locked) ?? nodes.find((n) => !n.locked);
  pick(first?.id ?? -1);
  return wrap;
};

/** The request board: concrete asks for builds (SPEC §10.2 "cumplir encargo"). */
export const boardPanel = (svc: CampaignService, cmd: Cmd, selected: { q: number; r: number } | null, rerender: () => void): HTMLElement => {
  const s = svc.state;
  const wrap = h('div.board');
  const list = h('div.tasks');
  const name = (pid: string): string => (pid === svc.me ? t('board.byYou') : t('board.by', { name: s.players.find((p) => p.id === pid)?.displayName ?? '—' }));
  const open = s.board.filter((b) => !b.done).slice().reverse();
  for (const task of open) {
    const want = (['wood', 'stone', 'crystal', 'dust'] as const).filter((k) => task.want[k] > 0);
    const left = { wood: task.want.wood - task.got.wood, stone: task.want.stone - task.got.stone, crystal: task.want.crystal - task.got.crystal, dust: task.want.dust - task.got.dust };
    const give = { wood: Math.min(left.wood, s.storage.wood), stone: Math.min(left.stone, s.storage.stone), crystal: Math.min(left.crystal, s.storage.crystal), dust: Math.min(left.dust, s.storage.dust) };
    const canGive = want.some((k) => give[k] > 0);
    list.append(
      h(
        'article.task',
        {},
        h('div.task-text', {}, task.text || t('board.noText')),
        h('div.small', {}, name(task.by)),
        h('div.task-res', {}, ...want.map((k) => h('span.cost-chip', {}, h('span', { html: ICONS[k] }), `${task.got[k]} / ${task.want[k]}`))),
        h('button.btn.small', { disabled: !canGive, onclick: async () => { if (await cmd({ t: 'fulfill', task: task.id, res: give })) rerender(); } }, t('board.fulfill')),
      ),
    );
  }
  if (!open.length) list.append(h('div.board-empty', {}, h('span', { html: ICONS.board }), h('span', {}, t('board.empty'))));
  const text = h('input.dark-input', { type: 'text', maxlength: 120, placeholder: t('board.text') }) as HTMLInputElement;
  const nums = (['wood', 'stone', 'crystal'] as const).map((k) => {
    const i = h('input.dark-input.num', { type: 'number', min: 0, max: 999, step: 10, inputmode: 'numeric', placeholder: '0', 'aria-label': t(`res.${k}`) }) as HTMLInputElement;
    return [k, i] as const;
  });
  // −/+ in steps of 10, so asking for materials works with a thumb too.
  const stepper = (k: 'wood' | 'stone' | 'crystal', i: HTMLInputElement): HTMLElement => {
    const bump = (d: number): void => {
      i.value = String(Math.max(0, Math.min(999, (Number(i.value) || 0) + d)) || '');
      i.dispatchEvent(new Event('input'));
    };
    return h(
      'div.stepper',
      { title: t(`res.${k}`) },
      h('span.st-ico', { html: ICONS[k] }),
      h('span.st-name', {}, t(`res.${k}`)),
      h('div.st-ctl', {}, h('button.st-b', { type: 'button', 'aria-label': '−10', onclick: () => bump(-10) }, '−'), i, h('button.st-b', { type: 'button', 'aria-label': '+10', onclick: () => bump(10) }, '+')),
    );
  };
  const post = h('button.btn', {
    onclick: async () => {
      const want = Object.fromEntries(nums.map(([k, i]) => [k, Math.max(0, Math.floor(Number(i.value) || 0))]));
      if (await cmd({ t: 'task', q: selected?.q ?? null, r: selected?.r ?? null, want, text: text.value })) rerender();
    },
  }, t('board.post'));
  const sync = (): void => {
    (post as HTMLButtonElement).disabled = !nums.some(([, i]) => Number(i.value) > 0);
  };
  for (const [, i] of nums) i.addEventListener('input', sync);
  sync();
  const form = h(
    'div.board-form',
    {},
    h('label.small', {}, t('board.textLabel')),
    text,
    h('label.small', {}, t('board.wantLabel')),
    h('div.steppers', {}, ...nums.map(([k, i]) => stepper(k, i))),
    h('div.actions', {}, post),
  );
  wrap.append(h('p.hint', {}, t('board.hint')), list, h('div.sec-title', {}, h('h4', {}, t('board.new'))), form);
  return wrap;
};

export type { CampaignState };
